import { SerializedData, Store, timeoutPromise } from "bernie-core";
import { action, observable } from "mobx";
import {
  Composition,
  ExtendedContextStore,
  FlexContext,
  TrackingContext,
  ViewModelResponse,
  FlexBffTemplateResponse,
  AjaxParams,
  AjaxQueryParams,
  Template,
} from "typings/flexFramework/FlexDefinitions";
import * as fetch from "isomorphic-fetch";
import { safeResponseToJson } from "components/utility/FetchUtils";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import {
  FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
  FAILED_MODULE_COMPOSITION_FETCH,
  FAILED_COMPOSITION_FETCH,
  FAILED_COMPOSITION_PARSE,
} from "config/systemEvents";
import { ContentFlexComponent } from "components/flexComponents/Content/typings";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import * as jsonBigInt from "json-bigint";
import { getNameFromFlexComponent } from "./getNameFromFlexComponent";
import { BlossomComponentTreeStore, NewBlossomComponent } from "stores/BlossomComponentTree";

const jsonBigIntParse = jsonBigInt({ storeAsString: true }).parse;

type IdentifierKey = "id" | "name" | "fmId";

export class CompositionStore extends Store {
  @observable
  public composition?: Composition;

  private blossomComponentsLogger: Logger;

  public pageHeading: string | null;
  public pageSubHeadline: string | null;
  public title: string;
  public templateId: string;
  public canonicalPath: string;

  private flexContext?: FlexContext;
  private trackingContext?: TrackingContext;
  public bffHostname: string;
  public bffTimeout: number;

  public constructor(
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
    blossomComponentLogger: Logger,
    config: any = {}
  ) {
    super(state, logger);
    this.blossomComponentsLogger = blossomComponentLogger;
    this.bffHostname = config?.lpt?.url ?? "https://lpt-blossom-bff.us-west-2.prod.expedia.com";
    this.bffTimeout = config?.timeout ?? 5000;
  }

  /* istandbul ignore next */
  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public isInFlexEnableGraphqlModulesTest = false; // set to true by FlexController if in correct test bucket, will be false for composition based rendering in all cases.

  /**
   * Initializes CompositionStore with data from other stores. Called from controller
   * @param  {ViewModelResponse|FlexBffTemplateResponse} bffResponse
   * @param  {ExtendedContextStore} context
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @param  {BlossomComponentTreeStore} blossomComponentTreeStore
   * @param  {StaticMapStore} mapStore
   * @returns void
   */
  @action
  public setup(
    bffResponse: ViewModelResponse | FlexBffTemplateResponse,
    context: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore,
    blossomComponentTreeStore: BlossomComponentTreeStore,
    mapStore: StaticMapStore
  ) {
    if (!bffResponse) {
      return;
    }

    const isCompositionResponse = (
      response: ViewModelResponse | FlexBffTemplateResponse
    ): response is ViewModelResponse => {
      return !!(response as ViewModelResponse).composition;
    };

    if (isCompositionResponse(bffResponse)) {
      this.composition = bffResponse.composition;
      this.pageHeading = bffResponse.composition.pageHeading;
      this.pageSubHeadline = bffResponse.composition.pageSubHeadline;
      this.title = bffResponse.composition.title;

      const newTreeFromComposition = this.composition.page.regionList.reduce((result, child) => {
        const childBlossomComponent = this.flexComponentToBlossomComponent(child, flexModuleModelStore, context);
        if (childBlossomComponent !== undefined) {
          result.push(childBlossomComponent);
        }

        return result;
      }, [] as NewBlossomComponent[]);

      blossomComponentTreeStore.setTree(newTreeFromComposition);
    }

    this.templateId = bffResponse.templateId;
    this.canonicalPath = bffResponse.canonicalPath;
    this.flexContext = bffResponse.flexContext;
    this.trackingContext = bffResponse.trackingContext;
    this.injectInterestFreePayLater();
    this.buildStaticMapImgUrl(context, mapStore, flexModuleModelStore);
  }

  /**
   * Populates FlexModuleModelStore from a composition for AJAX updates and cases when CompositionStore will not be instantiating a BlossomComponentTree
   * @param flexModuleModelStore
   * @param context
   */
  @action
  public populateFlexModuleModelStoreFromComposition(
    flexModuleModelStore: FlexModuleModelStore,
    context: ExtendedContextStore
  ) {
    if (flexModuleModelStore.modelMap.size > 0 || !this.composition) {
      // modelMap is already populated
      return;
    }

    this.composition.page.regionList.forEach((region) => {
      // flexModuleModelStore is populated in the BlossomComponent constructor
      this.flexComponentToBlossomComponent(region, flexModuleModelStore, context);
    });
  }

  /**
   * creates NewBlossomComponent equivalent of provided FlexComponent
   * @param  {FlexComponent} flexComponent
   * @param  {FlexModuleModelStore} flexModuleModelStore
   * @param  {ExtendedContextStore} context
   * @returns NewBlossomComponent|undefined
   */
  private flexComponentToBlossomComponent(
    flexComponent: FlexComponent,
    flexModuleModelStore: FlexModuleModelStore,
    context: ExtendedContextStore
  ): NewBlossomComponent | undefined {
    const name = getNameFromFlexComponent(flexComponent);
    const id = flexComponent.type === "Module" ? flexComponent.id : flexComponent.model?.id;

    // gather and create decendant BlossomComponents
    const childFlexComponents: FlexComponent[] = [
      ...(flexComponent.modules || []),
      ...(flexComponent.groups || []),
      ...(flexComponent.children || []),
    ];
    const children = childFlexComponents.reduce((result, child) => {
      const childBlossomComponent = this.flexComponentToBlossomComponent(child, flexModuleModelStore, context);
      if (childBlossomComponent !== undefined) {
        result.push(childBlossomComponent);
      }

      return result;
    }, [] as NewBlossomComponent[]);

    //TODO: what should responsePending be for regions, tab groups, tabs?
    return new NewBlossomComponent({
      componentData: {
        id,
        name,
        fmId: flexComponent.fmId,
        fmTitleId: flexComponent.fmTitleId,
        view: flexComponent.model?.view,
        scene: flexComponent.model?.scene,
        children,
        flexModuleModel: {
          responsePending: !flexComponent.model || flexComponent.model.ajaxOnly ? true : false,
          error: !!flexComponent.model?.error,
          model: flexComponent.model,
        },
      },
      context,
      flexModuleModelStore,
      logger: this.blossomComponentsLogger,
    });
  }

  /**
   * Fetches a Flex Composition from FlexBFF, populating the CompositionStore with a composition and a populated moduleMap
   *
   * @param template a Flex template
   * @returns Promise<void>
   */
  @action
  public fetchCompositionFromTemplate = (
    template: Template,
    context: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore,
    headers: any
  ) =>
    new Promise((resolve, reject) => {
      const requestBody = {
        flexContext: this.flexContext,
        template,
        trackingContext: this.trackingContext,
        templateId: this.templateId,
      };

      const fetchOptions: RequestInit = {
        method: "POST",
        headers: {
          ...headers,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      };

      timeoutPromise(
        fetch(`${this.bffHostname}/bff/composition`, fetchOptions),
        this.bffTimeout,
        "composition fetch timeout. Retrying."
      )
        .then((response) => response.text())
        .then((body) => {
          try {
            this.composition = jsonBigIntParse(body).composition;
            this.populateFlexModuleModelStoreFromComposition(flexModuleModelStore, context);
            resolve();

            return;
          } catch (err) {
            this.logger.logEvent(
              FAILED_COMPOSITION_PARSE,
              "Unexpected error when parsing composition from template",
              err
            );
            throw err;
          }
        })
        .catch((err) => {
          this.logger.logEvent(
            FAILED_COMPOSITION_FETCH,
            "Unexpected error when fetching composition from template",
            err
          );
          reject(err);

          return;
        });
    });

  public buildStaticMapImgUrl = (
    context: ExtendedContextStore,
    mapStore: StaticMapStore,
    flexModuleModelStore: FlexModuleModelStore
  ) => {
    const hotelsModel = flexModuleModelStore.modelMap.get("hotels-1")?.model as any;

    if (hotelsModel) {
      mapStore.generateGoogleStaticMapSrcForHotels(context, hotelsModel.hotels);
    }
  };

  public fetchModelWithSpecificParams = async (fmId: number, specificParams: AjaxParams) => {
    const options = {
      tId: this.templateId,
      fmId,
      url: this.buildUrlQueryParam(),
      cache: "false",
      epoch: Date.now().toString(),
      viewName: "json",
      clientId: "blossom",
      userAttributes: "blossom",
    };

    const endpointUrl = this.buildAjaxEndpointUrl(options, specificParams);

    if (endpointUrl && fmId) {
      return fetch(endpointUrl, { method: "GET" }).then(safeResponseToJson);
    }
  };

  /**
   * Fetches the module that has the passed fmId the ajax will return the new model for the requested module.
   * This method will trigger the update of the composition based on the returned ajax response.
   *
   * @param fmId a string representing the module id
   * @param locationUserId new location value, this will be updated with the passed context.
   */
  public fetchModuleAndUpdateCompositionFromFmId = async (fmId: number, locationUserId?: string) => {
    return this.fetchModuleFromFmId(fmId, locationUserId)
      .then((moduleBody: FlexComponent) => {
        if (moduleBody) {
          const regions = this.composition?.page?.regionList;
          if (!regions) {
            return Promise.resolve({
              status: 500,
            });
          }
          this.updateCompositionFromFmId(regions, fmId, "fmId", moduleBody);
        }

        return Promise.resolve({
          status: 200,
        });
      })
      .catch((err) => {
        this.logger.logEvent(
          FAILED_MODULE_COMPOSITION_FETCH,
          "Unexpected error when fetching composition for module",
          err
        );

        return Promise.resolve({
          status: 500,
        });
      });
  };

  /**
   * Triggers the ajax call to lpt-flex-web to request a certain module by its module id
   *
   * @param fmId a string id representing the module id
   * @param locationUserId new location value, this will be updated with the passed context.
   * @returns the ajax response model
   */
  public fetchModuleFromFmId = async (fmId: number, locationUserId?: string) => {
    const specificParams = {
      locationUserId,
      path: "/lpt/flex/ajax",
      event: FAILED_MODULE_COMPOSITION_FETCH,
      loggerArgs: `Error when fetching composition for module due to missing values for templateId=${this.templateId}, fmId=${fmId}`,
    };

    return this.fetchModelWithSpecificParams(fmId, specificParams);
  };

  /**
   * Fetches modules that belong to a scene, and update of the composition the ajax will return values for the modules
   * that belong to the passed scene. It will return empty and set ajaxOnly=true to the modules that belong to a
   * different scene.
   *
   * @param scene a string id representing the scene attribute value
   */
  public fetchModuleAndUpdateCompositionFromSceneId = async (scene: string) => {
    return this.fetchModuleFromSceneId(scene)
      .then((components: FlexComponent[]) => {
        if (this.composition) {
          const regions = this.composition?.page?.regionList;

          components
            .filter((comp) => comp)
            .forEach((definedComp) => this.updateCompositionFromFmId(regions, definedComp.fmId, "fmId", definedComp));
        }
      })

      .catch((err) => {
        this.logger.logEvent(
          FAILED_MODULE_COMPOSITION_FETCH,
          "Unexpected error when fetching composition for scene",
          err
        );
      });
  };

  /**
   * Triggers the ajax call to lpt-flex-web to request modules that have a scene attribute, ajax will depend on the passed
   * sceneId param to return values for the modules that belong to the passed scene and to return empty and
   * set ajaxOnly=true to the modules that belong to a diff scene.
   *
   * @param sceneId a string id representing the scene attribute value
   * @returns the ajax response model
   */
  private fetchModuleFromSceneId = async (sceneId: string) => {
    const options = {
      tId: this.templateId,
      scene: sceneId,
    };

    const specificParams = {
      path: "/lpt/flex/reactive",
      event: FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
      loggerArgs: `Error when fetching reactive modules for scene due to missing values for templateId=${this.templateId}, scene=${sceneId}`,
    };

    const endpointUrl = this.buildAjaxEndpointUrl(options, specificParams);

    if (endpointUrl && sceneId) {
      return fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(this.flexContext),
      }).then(safeResponseToJson);
    }
  };

  /**
   * Given the new value for the component this method will update its value in the stored composition.
   *
   * @param regions the composition model
   * @param keyValue the id, name, fmId of the component to be updated
   * @param key determines whether to update the component by its id,name,fmId
   * @param component the new component to be updated: In the case where the model is sent it will update the model of
   * the component in the composition that has the same passed id. In case where the entire flexComponent is sent it will
   * fetch the model form it and update the model of the component in the composition that has the same passed id.
   */
  public updateCompositionFromFmId = (
    regions: FlexComponent[],
    keyValue: string | number,
    key: IdentifierKey,
    component: FlexComponent
  ) => {
    let componentToUpdate: FlexComponent | undefined;
    regions.find((region) => {
      if (region) {
        componentToUpdate = findComponentByIdOrNameInComposition(region, key, keyValue);
      }

      return !!componentToUpdate;
    });

    if (componentToUpdate) {
      componentToUpdate.model = component && component.model ? component.model : component;
    }

    return;
  };

  /**
   * Finds out if the region should be displayed or not based on: if all its children are ajax only then it will be hidden.
   *
   * @param regionFmId - the regional module id
   * @returns boolean - should the region be displayed
   */
  public shouldDisplayRegion(regionFmId: number): boolean {
    if (this.composition === undefined) {
      return false;
    }

    let shouldDisplayRegion = false;
    let requestedRegion: FlexComponent | undefined;
    const regions = this.composition?.page?.regionList;
    requestedRegion = findRegionByIdInComposition(regions, "fmId", regionFmId);
    if (requestedRegion) {
      requestedRegion.modules.forEach((module) => {
        if (module.model.ajaxOnly !== true) {
          shouldDisplayRegion = true;
        }
      });
    }

    return shouldDisplayRegion;
  }

  private buildAjaxEndpointUrl(options: AjaxQueryParams, specificParams: AjaxParams) {
    if (!options.tId || (!options.fmId && !options.scene)) {
      this.logger.logEvent(specificParams.event, specificParams.loggerArgs, JSON.stringify(options));

      return;
    }

    if (specificParams.locationUserId) {
      options["locationUserId"] = specificParams.locationUserId;
    }
    if (specificParams.flightClass) {
      options["flightClass"] = specificParams.flightClass;
    }
    if (specificParams.lengthOfStay) {
      options["lengthOfStay"] = specificParams.lengthOfStay;
    }
    if (specificParams.starRating) {
      options["starRating"] = specificParams.starRating;
    }

    let queryParams = "";
    Object.keys(options)
      .filter((key) => options[key])
      .forEach((key, index) => {
        if (key) {
          queryParams = index === 0 ? `?${key}=${options[key]}` : `${queryParams}&${key}=${options[key]}`;
        }
      });

    return `${specificParams.path}${queryParams}`;
  }

  private buildUrlQueryParam(): string | undefined {
    if (this.canonicalPath) {
      const tName = getQueryParamValue("tName");
      let urlQueryParam = this.canonicalPath;

      if (this.canonicalPath === "/") {
        urlQueryParam = this.canonicalPath;
      } else if (this.canonicalPath.startsWith("/")) {
        urlQueryParam = this.canonicalPath.substr(1);
      }

      if (tName) {
        urlQueryParam = `${urlQueryParam}?tName=${tName}`;
      }

      return encodeURI(urlQueryParam);
    }

    return undefined;
  }

  // Temporary hack to add required EXP_MX (TPID 12) /EXP_BR (TPID 69) Legal & Loyalty Information
  // This assumes that the region is called Wizard and page type is HTG
  private injectInterestFreePayLater = () => {
    const tpid = this.flexContext?.siteContext.tpid;
    if (
      (tpid === 12 || tpid === 69) &&
      this.flexContext &&
      this.flexContext.searchContext &&
      this.flexContext.searchContext.pageType === "Travel-Guide-Hotels"
    ) {
      const wizardRegion =
        this.composition &&
        this.composition.page &&
        this.composition.page.regionList &&
        this.composition.page.regionList.filter((region) => region.name === "wizard")[0];

      if (wizardRegion) {
        const editorials: ContentFlexComponent[] = wizardRegion && wizardRegion.modules;
        const messaging = tpid === 12 ? "Hoteles en hasta 18 MSI" : "Hotéis em até 12x sem juros";

        const injectedflexComponent: ContentFlexComponent = {
          model: {
            contentPurpose: "ConfidenceMessage",
            items: [{ text: messaging }],
          },
          type: "Module",
          name: "editorial",
        } as any;
        wizardRegion.modules = [...editorials, injectedflexComponent];
      }
    }
  };

  /**
   * removes data that will be re-constructed client side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    const keysToIgnore = ["flexContext", "trackingContext", "bffHostname", "blossomComponentsLogger"];
    /* istanbul ignore next */
    if (this.isInFlexEnableGraphqlModulesTest) {
      return super.toJSON([...keysToIgnore, "composition"]);
    }
    //TODO after hybrid blossom refactor is complete, exclude composition from serialization

    return super.toJSON(keysToIgnore);
  }
}

/**
 * Recurses over the composition until it finds a component
 * @param component a flex component, could be either a region or a module
 * @param key the identifier used to search the component, i.e. id or name
 * @param keyValue the identifier value
 */
export function findComponentByIdOrNameInComposition(
  component: FlexComponent,
  key: IdentifierKey,
  keyValue: string | number
): FlexComponent | undefined {
  if (component[key] === keyValue) {
    return component;
  }

  if (component.type === "Region" && component.modules) {
    let recursionResult;
    for (const childComponent of component.modules) {
      recursionResult = findComponentByIdOrNameInComposition(childComponent as FlexComponent, key, keyValue);
      if (recursionResult) {
        return recursionResult;
      }
    }
  }

  return undefined;
}

/**
 * Recurses over the composition until it finds a region
 * @param component a flex component array representing regions
 * @param key the identifier used to search the component, i.e. id or name
 * @param keyValue the identifier value
 */
export function findRegionByIdInComposition(
  regions: FlexComponent[],
  key: IdentifierKey,
  keyValue: string | number
): FlexComponent | undefined {
  let requestedRegion: FlexComponent | undefined;
  regions.forEach((region) => {
    const foundRegion = findComponentByIdOrNameInComposition(region, "fmId", keyValue);
    if (foundRegion && foundRegion.model[key] === keyValue) {
      requestedRegion = foundRegion;
    }
  });

  return requestedRegion;
}
